// ABOUTME: Builds the table a catalog range is shown as when its category is set to the `table` render type.
// ABOUTME: Product (name with image and code), Specifications (the features, narrowing filters), Price; the cart in the row actions.

import type {
    ICategory,
    IModelSelectionData,
    IProduct,
    IProductTableListProps,
} from '@coolshop/cool-model-selection';
import {
    Box,
    IconButton,
    InfoOutlinedIcon,
    SettingsOutlinedIcon,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import type {
    ContentType,
    CustomCellRendererProps,
    DataGridAction,
    SearchNode,
} from '@ds/data-table';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { CatalogThumbnail } from './CatalogThumbnail';
import { getProductImageUrl } from './getProductImageUrl';
import { ProductDetailPageAction } from './ProductDetailPageAction';
import { ProductFavoriteAction } from './ProductFavoriteAction';
import { RangeTableCartStepper } from './RangeTableCartStepper';

/** A column of a table category, as the model selection document sends it: a feature. */
export interface RangeTableColumn {
    code: string;
    label: string;
    type: string;
}

/** The table presentation the model selection document adds to a category. */
type TableCategory = ICategory & {
    renderType?: string;
    tableColumns?: RangeTableColumn[];
    /** Whether the list price column shows, as the category's `show_list_price` flag; unset hides it. */
    showListPrice?: boolean;
};

/**
 * A product of a table category, with its values for the category's feature columns: the option
 * values of its model, several for a feature with several options.
 */
type TableProduct = IProduct & { tableValues?: Record<string, string[] | string> };

// Column codes are read back from the filter field names, which the table splits on `-` and `.`:
// the fixed ones avoid both, as the feature columns' positional codes do.
const NAME_COLUMN = 'productName';
const PRICE_COLUMN = 'listPrice';

// The column groups, whose codes prefix the ids of their columns (`<group>-<code>`).
const PRODUCT_GROUP = 'product';
const SPECIFICATIONS_GROUP = 'specifications';
const PRICE_GROUP = 'price';

const LANGUAGE = 'en';

// As the catalog cards: a product is configurable unless the document says it is not.
const isConfigurable = (product: IProduct) =>
    (product as IProduct & { isConfigurable?: boolean }).isConfigurable ?? true;

const columnName = (label: string) => [{ languageIsoCode: LANGUAGE, value: label }];

/**
 * A column whose filter is an autocomplete picking several values: the table gives a `link` column
 * one, and asks its options of the table (`getHierarchyNodes`) by the column's `hierarchy`, which the
 * table answers from the options `loadProducts` returns for that column's code.
 */
const pickColumn = (code: string, label: string): ContentType =>
    ({
        code,
        name: columnName(label),
        type: 'link',
        hierarchy: code,
        mandatory: false,
        multiLanguage: false,
        multiValue: true,
        sortable: true,
    } as ContentType);

// The price: sorted by its amount, with no filter (`hideFilters` below).
const priceColumn = (label: string): ContentType =>
    ({
        code: PRICE_COLUMN,
        name: columnName(label),
        type: 'text',
        mandatory: false,
        multiLanguage: false,
        multiValue: false,
        sortable: true,
    } as ContentType);

// Side of the product image beside its name, as the cart line images.
const IMAGE_SIZE = '40px';

const firstValue = (value: unknown) =>
    (value as Array<{ value?: string[] }>)?.[0]?.value?.[0] || undefined;

// The name cell with the product image beside it and the product code under the name. The table
// picks a cell's renderer by the type stored on its value, so the name keeps its own column
// (filtered and sorted by the name) and only its value is marked with this type. The table hands a
// renderer its own row, which holds the values of its columns only: the image and the code, which no
// column shows, are read from the product of the row's id, so the renderer is made for each range's
// products. The code shares the name's cell rather than a column of its own: the table places a
// second column pinned on the left by its declared width, narrower than the one it draws, so the
// second one slid over the first while the table scrolls.
const NAME_WITH_IMAGE_TYPE = 'productNameWithImage';
const nameWithImageCell = (productOf: (productId: string) => IProduct | undefined) =>
    function NameWithImageCell({ value, row }: CustomCellRendererProps) {
        const productId = (row as { id?: string } | undefined)?.id;
        const product = productId ? productOf(productId) : undefined;
        return (
            // The cell keeps a narrow right padding of its own: the longest name sets the column
            // width, so the padding parts it from the next column.
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0} pr={2}>
                <CatalogThumbnail
                    url={product ? getProductImageUrl(product) : undefined}
                    alt=""
                    size={IMAGE_SIZE}
                />
                <Box minWidth={0}>
                    {/* The table's own text cells are subtitle2. */}
                    <Typography variant="subtitle2" noWrap>
                        {firstValue(value)}
                    </Typography>
                    {product?.internalId && (
                        // A caption is inline: the code keeps a line of its own.
                        <Typography variant="caption" display="block" color="text.secondary" noWrap>
                            {product.internalId}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

// The product group (the name, with its image and code) stays on the left, the price and the row
// actions on the right while the specifications scroll: a pinned group spanning scrolling columns
// would show its band twice. The actions column is given the width its content takes: sized at zero,
// the price pinned beside it would slide under it. The table names a content column
// `<group>-<code>` and the actions column `actions`.
// With the cart stepper of a product added as is, or icons only (favorite, info, configure) when
// every product of the range is configured.
const ACTIONS_COLUMN_SIZE = 200;
const ICON_ACTIONS_COLUMN_SIZE = 144;
const colsConfig = (
    showPrice: boolean,
    actionsColumnSize: number,
): IProductTableListProps['colsConfig'] => ({
    actionsColumnSize,
    pinning: {
        columnPinning: {
            left: [`${PRODUCT_GROUP}-${NAME_COLUMN}`],
            right: showPrice ? [`${PRICE_GROUP}-${PRICE_COLUMN}`, 'actions'] : ['actions'],
        },
    },
    // The price is sorted, never filtered.
    customColsHeaders: { [`${PRICE_GROUP}-${PRICE_COLUMN}`]: { hideFilters: true } },
    // The name column is as wide as its longest name, so no name is cut short.
    columns: { [`${PRODUCT_GROUP}-${NAME_COLUMN}`]: { maxWidth: false } },
});
// Built once each: a new object would reset the table's column state.
const COLS_CONFIGS = {
    withPrice: {
        stepper: colsConfig(true, ACTIONS_COLUMN_SIZE),
        icons: colsConfig(true, ICON_ACTIONS_COLUMN_SIZE),
    },
    withoutPrice: {
        stepper: colsConfig(false, ACTIONS_COLUMN_SIZE),
        icons: colsConfig(false, ICON_ACTIONS_COLUMN_SIZE),
    },
};

const groupOf = (code: string) => {
    if (code === NAME_COLUMN) return PRODUCT_GROUP;
    if (code === PRICE_COLUMN) return PRICE_GROUP;
    return SPECIFICATIONS_GROUP;
};

const textContent = (contentType: string, value: string, type = 'text') => ({
    contentType,
    contentTypeGroup: groupOf(contentType),
    contentTypeGroupIndex: 0,
    type,
    inherited: false,
    value: [{ languageIsoCode: LANGUAGE, value: [value] }],
});

// The values a filter asks for: a picking filter sends a list, any other its one value.
const wantedValues = (value: string | string[] | undefined): string[] =>
    (Array.isArray(value) ? value : [value]).filter((item): item is string => !!item);

const byText = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/**
 * The `rangeTableProps` of the model selection: for a range whose category has the `table` render
 * type, the props of the table its products are listed in; for any other range `undefined`, so it
 * keeps its product list. The table pages, filters and sorts the range's products in the browser:
 * the document already holds them all. The name (with the product image), the code and the
 * category's features are filtered by picking values; each filter offers the values of the products
 * the other filters leave, so the choices narrow as the user filters. `onAddProduct` adds the
 * quantity picked on the row of a product that needs no configuration; a configurable product's row
 * opens the configurator through `onConfigureProduct` instead, as a click on its card does. With
 * `bindToCart` (default) the row shows and changes the product's quantity in the cart; without it
 * the row only adds.
 */
export function useCatalogRangeTable(
    onAddProduct: ((product: IProduct, quantity: number) => Promise<void>) | undefined,
    onConfigureProduct: (product: IProduct) => void,
    bindToCart = true,
    showFavorite = false,
) {
    const { formatMessage, formatNumber } = useIntl();

    const labels = useMemo(
        () => ({
            name: formatMessage({ id: 'cpq.catalog.table.name', defaultMessage: 'Name' }),
            price: formatMessage({
                id: 'cpq.catalog.table.list-price',
                defaultMessage: 'List Price',
            }),
            productGroup: formatMessage({
                id: 'cpq.catalog.table.group.product',
                defaultMessage: 'Product',
            }),
            specificationsGroup: formatMessage({
                id: 'cpq.catalog.table.group.specifications',
                defaultMessage: 'Specifications',
            }),
            priceGroup: formatMessage({
                id: 'cpq.catalog.table.group.price',
                defaultMessage: 'Price',
            }),
        }),
        [formatMessage],
    );

    // The rows call the latest callbacks through refs: the host rebuilds them whenever the cart
    // changes, and new props would reload the table, losing its page and scroll at every add.
    const onAddRef = useRef(onAddProduct);
    onAddRef.current = onAddProduct;
    const onConfigureRef = useRef(onConfigureProduct);
    onConfigureRef.current = onConfigureProduct;
    const canAdd = !!onAddProduct;

    // One set of props per range object, as the table reloads whenever `loadProducts` changes; it
    // starts afresh when what the props are built from changes.
    const cache = useMemo(
        () => new WeakMap<ICategory, IProductTableListProps>(),
        [canAdd, bindToCart, showFavorite, labels, formatNumber],
    );

    return useCallback(
        (range: ICategory): IProductTableListProps | undefined => {
            const category = range as TableCategory;
            if (!canAdd || category.renderType !== 'table') return undefined;
            const cached = cache.get(range);
            if (cached) return cached;

            const products = (range.products ?? []) as TableProduct[];
            const productById = new Map(products.map(product => [product.id, product]));
            const featureColumns = category.tableColumns ?? [];
            // The list price column shows only when the category asks for it; the name, with the
            // code under it, always shows.
            const showPrice = category.showListPrice === true;
            // The columns filtered by picking values: the name and the features.
            const pickCodes = [NAME_COLUMN, ...featureColumns.map(c => c.code)];
            const configureLabel = formatMessage({
                id: 'cpq.catalog.table.configure',
                defaultMessage: 'Configure',
            });

            // The values of a product in a picking column, as its filter matches and offers them.
            const valuesOf = (product: TableProduct, code: string): string[] => {
                if (code === NAME_COLUMN) {
                    const name = product.modelName || product.description;
                    return name ? [name] : [];
                }
                const values = product.tableValues?.[code];
                if (Array.isArray(values)) return values;
                return values ? [values] : [];
            };
            const cellValue = (product: TableProduct, code: string): string => {
                if (code === PRICE_COLUMN) {
                    return product.listPrice
                        ? formatNumber(product.listPrice, {
                              style: 'currency',
                              currency: product.currency || 'EUR',
                          })
                        : '';
                }
                return valuesOf(product, code).join(', ');
            };

            const mapProductToRow = (product: IProduct): SearchNode =>
                ({
                    id: product.id,
                    code: product.id,
                    hierarchy: 'productData',
                    hierarchyCode: 'productData',
                    parent: 'root',
                    parentCode: 'root',
                    listName: product.internalId,
                    disabled: false,
                    nodePublished: 'PUBLISHED',
                    createdAt: 0,
                    updatedAt: 0,
                    contents: [...pickCodes, ...(showPrice ? [PRICE_COLUMN] : [])].map(code =>
                        textContent(
                            code,
                            cellValue(product as TableProduct, code),
                            code === NAME_COLUMN ? NAME_WITH_IMAGE_TYPE : undefined,
                        ),
                    ),
                } as unknown as SearchNode);

            const loadProducts: IProductTableListProps['loadProducts'] = async ({
                filters,
                sort,
                page,
                pageSize,
            }) => {
                const wanted = new Map(
                    pickCodes.map(code => [code, wantedValues(filters[code])] as const),
                );
                const passes = (product: TableProduct, code: string) => {
                    const picked = wanted.get(code) ?? [];
                    return (
                        picked.length === 0 ||
                        valuesOf(product, code).some(value => picked.includes(value))
                    );
                };
                const passesAll = (product: TableProduct, except?: string) =>
                    pickCodes.every(code => code === except || passes(product, code));
                const matching = products.filter(product => passesAll(product));

                // Each filter offers the values of the products the other filters leave, the ones
                // it has picked included, so a pick never strands the user without its option.
                const options = Object.fromEntries(
                    pickCodes.map(code => {
                        const values = new Set(wanted.get(code));
                        products
                            .filter(product => passesAll(product, code))
                            .forEach(product =>
                                valuesOf(product, code).forEach(value => values.add(value)),
                            );
                        return [
                            code,
                            [...values].sort(byText).map(value => ({ code: value, value })),
                        ];
                    }),
                );

                const [order] = sort ?? [];
                const sorted = order
                    ? [...matching].sort((a, b) => {
                          // A price sorts by its amount, every other column by its text.
                          const compared =
                              order.field === PRICE_COLUMN
                                  ? a.listPrice - b.listPrice
                                  : byText(cellValue(a, order.field), cellValue(b, order.field));
                          return order.sort === 'DESC' ? -compared : compared;
                      })
                    : matching;
                // The table counts its pages from 0: the first page is `page` 0.
                const start = page * pageSize;
                return {
                    categories: [
                        {
                            ...range,
                            products: sorted.slice(start, start + pageSize),
                            count: sorted.length,
                            hasNextPage: start + pageSize < sorted.length,
                        },
                    ],
                    filters: options,
                } as IModelSelectionData;
            };

            // The model's favorite star, as on the catalog cards.
            const favoriteAction: DataGridAction = {
                key: 'favorite',
                renderElement: node => {
                    const product = productById.get(node.id);
                    return product ? <ProductFavoriteAction {...product} /> : null;
                },
            };
            const actions: DataGridAction[] = [
                ...(showFavorite ? [favoriteAction] : []),
                {
                    key: 'info',
                    renderElement: node => {
                        const product = productById.get(node.id);
                        return product ? (
                            <ProductDetailPageAction
                                {...product}
                                icon={<InfoOutlinedIcon fontSize="small" />}
                            />
                        ) : null;
                    },
                },
                {
                    key: 'add',
                    renderElement: node => {
                        const product = productById.get(node.id);
                        if (!product) return null;
                        // A product to configure has no quantity to pick: the configurator decides.
                        return isConfigurable(product) ? (
                            <Tooltip title={configureLabel}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label={configureLabel}
                                    onClick={() => onConfigureRef.current(product)}
                                >
                                    <SettingsOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <RangeTableCartStepper
                                productId={decodeURIComponent(atob(product.id))}
                                onAdd={async quantity => onAddRef.current?.(product, quantity)}
                                bindToCart={bindToCart}
                            />
                        );
                    },
                },
            ];

            const props: IProductTableListProps = {
                // A category without features has no specifications to show.
                columnGroups: [
                    {
                        code: PRODUCT_GROUP,
                        label: labels.productGroup,
                        columns: [pickColumn(NAME_COLUMN, labels.name)],
                    },
                    {
                        code: SPECIFICATIONS_GROUP,
                        label: labels.specificationsGroup,
                        columns: featureColumns.map(column =>
                            pickColumn(column.code, column.label),
                        ),
                    },
                    {
                        code: PRICE_GROUP,
                        label: labels.priceGroup,
                        columns: showPrice ? [priceColumn(labels.price)] : [],
                    },
                ].filter(group => group.columns.length > 0),
                customCellRenderers: {
                    [NAME_WITH_IMAGE_TYPE]: nameWithImageCell(productId =>
                        productById.get(productId),
                    ),
                },
                colsConfig:
                    COLS_CONFIGS[showPrice ? 'withPrice' : 'withoutPrice'][
                        products.every(isConfigurable) ? 'icons' : 'stepper'
                    ],
                mapProductToRow,
                loadProducts,
                actions,
                // The picking filters list the range's values as soon as they open.
                staticFilterOptions: true,
            };
            cache.set(range, props);
            return props;
        },
        [canAdd, bindToCart, showFavorite, labels, formatNumber, formatMessage, cache],
    );
}
